/**
 * Country Ticker Animation
 * Similar to LogoMarquee but with slower animation speed
 */

export default class CountryTicker {
    constructor() {
        this.containers = document.querySelectorAll('.country-marquee-container');
        // Direction: 'ltr' (left to right) or 'rtl' (right to left)
        this.direction = 'ltr';
        this.init();
    }

    init() {
        if (this.containers.length === 0) return;

        this.containers.forEach(container => {
            this.setupMarquee(container);
        });

        // Handle resize
        window.addEventListener('resize', this.handleResize.bind(this));
    }

    setupMarquee(container) {
        const wrapper = container.querySelector('.flex.relative.w-max');
        const originalContent = container.querySelector('.marquee-original');

        if (!wrapper || !originalContent) return;

        // Get content width for animation
        const contentWidth = originalContent.offsetWidth;

        // Create duplicates for seamless loop
        const duplicationsNeeded = Math.max(3, Math.ceil(window.innerWidth / contentWidth * 2));

        // Remove existing duplicates
        const existingDuplicates = wrapper.querySelectorAll('.marquee-duplicate');
        existingDuplicates.forEach(el => el.remove());

        // Create new duplicates
        for (let i = 0; i < duplicationsNeeded; i++) {
            const clone = originalContent.cloneNode(true);
            clone.classList.add('marquee-duplicate');
            clone.classList.remove('marquee-original');
            wrapper.appendChild(clone);
        }

        // Apply CSS animation
        this.applyAnimation(wrapper, contentWidth);

        // Fade in content
        setTimeout(() => {
            const allContent = wrapper.querySelectorAll('.marquee-original, .marquee-duplicate');
            allContent.forEach(el => {
                el.style.opacity = '1';
                el.style.transition = 'opacity 0.8s ease';
            });
        }, 100);
    }

    applyAnimation(wrapper, contentWidth) {
        // Calculate animation duration - slower than logo marquee
        // Logo uses contentWidth / 50, country ticker uses contentWidth / 40 for slower speed
        const duration = Math.max(30, contentWidth / 40);

        // Apply CSS animation
        wrapper.style.animation = `countryMarqueeScroll ${duration}s linear infinite`;

        // Add CSS keyframes if not already present
        this.addKeyframes(contentWidth);
    }

    addKeyframes(contentWidth) {
        const styleId = 'country-ticker-keyframes';
        let styleElement = document.getElementById(styleId);

        if (!styleElement) {
            styleElement = document.createElement('style');
            styleElement.id = styleId;
            document.head.appendChild(styleElement);
        }

        const startPos = this.direction === 'ltr' ? `-${contentWidth}px` : '0';
        const endPos = this.direction === 'ltr' ? '0' : `-${contentWidth}px`;

        styleElement.textContent = `
            @keyframes countryMarqueeScroll {
                0% { transform: translateX(${startPos}); }
                100% { transform: translateX(${endPos}); }
            }

            .country-marquee-container .marquee-original,
            .country-marquee-container .marquee-duplicate {
                opacity: 0;
                transition: opacity 0.8s ease;
            }

            @media (prefers-reduced-motion: reduce) {
                .country-marquee-container .flex.relative.w-max {
                    animation-play-state: paused !important;
                    transform: translateX(0) !important;
                }
            }
        `;
    }

    handleResize() {
        // Debounce resize
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
            this.containers.forEach(container => {
                this.setupMarquee(container);
            });
        }, 250);
    }

    destroy() {
        window.removeEventListener('resize', this.handleResize.bind(this));

        // Remove keyframes
        const styleElement = document.getElementById('country-ticker-keyframes');
        if (styleElement) {
            styleElement.remove();
        }

        // Reset containers
        this.containers.forEach(container => {
            const wrapper = container.querySelector('.flex.relative.w-max');
            if (wrapper) {
                wrapper.style.animation = '';
                const duplicates = wrapper.querySelectorAll('.marquee-duplicate');
                duplicates.forEach(el => el.remove());
            }
        });
    }
}
